/**
 * Página de Faturamento - Estilo Minimalista
 * Gerencia receitas e faturamentos da clínica
 */

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
  Grid,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  PersonAdd as PersonAddIcon,
  MedicalServices as MedicalServicesIcon,
  Search as SearchIcon,
  Cancel as CancelIcon,
  CreditCard as CreditCardIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { listarFaturamento, criarFaturamento, atualizarFaturamento, deletarFaturamento, emitirNotaFiscal, statusNotaFiscal, downloadReciboPF } from '../../services/faturamentoService';
import pacienteService from '../../services/pacienteService';
import procedimentoService from '../../services/procedimentoService';
import maquinaCartaoService from '../../services/maquinaCartaoService';
import { BANDEIRAS, acharTaxa, parcelasDisponiveis } from '../../utils/taxaCartao';
import BandeiraIcone from '../../components/BandeiraIcone';
import clinicaService from '../../services/clinicaService';
import { getMetricas } from '../../services/dashboardService';

const Faturamento = () => {
  const { user } = useAuth();
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  console.log('Usuário logado:', user); // DEBUG: Veja no console o objeto completo
  // Usa o campo correto do objeto user
  const planoAtual = user?.plano || user?.clinica?.plano || 'FREE';
  const tipoUsuario = user?.tipoPessoa || 'PF';
  const isHibrido = String(tipoUsuario).toUpperCase() === 'HIBRIDO';
  // Limite de faturamentos por mês no plano FREE
  const limiteFaturamentoFree = 15;
  const [faturamentos, setFaturamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [alertClinica, setAlertClinica] = useState({ open: false, campos: [] });

  // Filtro de mês/ano
  const [mesFiltro, setMesFiltro] = useState(new Date().getMonth() + 1);
  const [anoFiltro, setAnoFiltro] = useState(new Date().getFullYear());

  // Cálculo do limite após inicialização dos states
  const faturamentosNoMes = faturamentos.filter(f => {
    const dataFaturamento = new Date(f.data + 'T00:00:00');
    return dataFaturamento.getMonth() + 1 === mesFiltro && dataFaturamento.getFullYear() === anoFiltro;
  });
  const atingiuLimiteFree = planoAtual === 'FREE' && faturamentosNoMes.length >= limiteFaturamentoFree;

  const [openModal, setOpenModal] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [itemEdicao, setItemEdicao] = useState(null);
  const [openExcluirModal, setOpenExcluirModal] = useState(false);
  const [itemExcluir, setItemExcluir] = useState(null);

  const [openNotaModal, setOpenNotaModal] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [emailPaciente, setEmailPaciente] = useState('');


  // ATENÇÃO: tipoUsuario deve ser 'PF', 'PJ' ou 'HIBRIDO' (string, case-insensitive)
  // Só mostra o campo 'Tipo de Pessoa' se for HIBRIDO

  const [pacientes, setPacientes] = useState([]);
  const [procedimentos, setProcedimentos] = useState([]);
  const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
  const [procedimentoSelecionado, setProcedimentoSelecionado] = useState(null);
  const [pagadorBeneficiario, setPagadorBeneficiario] = useState(true);
  const [pagadorSelecionado, setPagadorSelecionado] = useState(null);
  const [descricaoPadraoNota, setDescricaoPadraoNota] = useState('');
  const [aliquotaPJ, setAliquotaPJ] = useState(null);

  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    paciente: '',
    cpf: '',
    formaPagamento: '',
    tipoPessoa: isHibrido ? '' : tipoUsuario, // Se híbrido, deixa vazio para escolher; senão, já define PF ou PJ
    observacoes: '',
    declarar: true,
    // Dados do pagador (quando diferente do beneficiário)
    pagadorNome: '',
    pagadorCpf: '',
    pagadorTipoPessoa: 'PF',
    pagadorCep: '',
    pagadorLogradouro: '',
    pagadorNumero: '',
    pagadorComplemento: '',
    pagadorBairro: '',
    pagadorCidade: '',
    pagadorEstado: '',
    // Taxa de máquina de cartão
    maquinaCartaoId: '',
    parcelasCartao: '',
    bandeiraCartao: '',
    cartaoAntecipado: false,
    taxaCartaoResponsavel: 'clinica',
  });

  const [maquinasCartao, setMaquinasCartao] = useState([]);

  const [pesquisa, setPesquisa] = useState('');
  const [paginaFaturamento, setPaginaFaturamento] = useState(1);
  const LIMITE_PAGINA = 10;

  const formasPagamento = ['Dinheiro', 'Cartão de Crédito', 'Cartão de Débito', 'PIX', 'Transferência', 'Cheque', 'Outros'];

  useEffect(() => {
    carregarDados();
    carregarPacientes();
    carregarProcedimentos();
    carregarMaquinasCartao();
    carregarDescricaoPadraoNota();
    carregarAliquotasReais();
  }, []);

  // Descrição padrão da nota (definida em /perfil) — usada pra montar o texto
  // sugerido em Observações quando o pagador é diferente do beneficiário.
  const carregarDescricaoPadraoNota = async () => {
    try {
      if (!user?.clinicaId) return;
      const clinica = await clinicaService.buscarClinica(user.clinicaId);
      setDescricaoPadraoNota(clinica?.descricaoPadraoNota || '');
    } catch (err) {
      console.error('Erro ao carregar descrição padrão da nota:', err);
    }
  };

  // Alíquota real de imposto (Simples Nacional ou Lucro Presumido, conforme
  // o regime da clínica) — usada na estimativa mostrada antes de emitir a
  // nota, no lugar de um percentual fixo genérico.
  const carregarAliquotasReais = async () => {
    try {
      const hoje = new Date();
      const metricas = await getMetricas(hoje.getMonth() + 1, hoje.getFullYear());
      if (metricas?.pessoaJuridica?.aliquotaEfetiva != null) {
        setAliquotaPJ(parseFloat(metricas.pessoaJuridica.aliquotaEfetiva));
      }
    } catch (err) {
      console.error('Erro ao carregar alíquota real:', err);
    }
  };

  // Quando o pagador é diferente do beneficiário, sugere em Observações a
  // descrição padrão + quem de fato recebeu o tratamento — sem sobrescrever
  // se o usuário já tiver digitado algo ali.
  useEffect(() => {
    if (!openModal || pagadorBeneficiario) return;
    if (!formData.paciente || !formData.paciente.trim()) return;
    setFormData((prev) => {
      if (prev.observacoes && prev.observacoes.trim()) return prev;
      const partes = [];
      if (descricaoPadraoNota) partes.push(descricaoPadraoNota);
      partes.push(`Beneficiário do tratamento: ${prev.paciente}${prev.cpf ? `, CPF ${prev.cpf}` : ''}`);
      return { ...prev, observacoes: partes.join(' - ') };
    });
  }, [openModal, pagadorBeneficiario, formData.paciente, formData.cpf, descricaoPadraoNota]);

  const carregarMaquinasCartao = async () => {
    try {
      const lista = await maquinaCartaoService.listar();
      setMaquinasCartao(Array.isArray(lista) ? lista : []);
    } catch (err) {
      console.error('Erro ao carregar máquinas de cartão:', err);
    }
  };

  // Derivados pra seção de taxa de cartão no formulário
  const maquinaCartaoSelecionada = maquinasCartao.find(m => m.id === formData.maquinaCartaoId) || null;
  const cartaoDebito = formData.formaPagamento === 'Cartão de Débito';
  const taxaCartaoSelecionada = maquinaCartaoSelecionada && (cartaoDebito || formData.parcelasCartao)
    ? acharTaxa(maquinaCartaoSelecionada, {
        modalidade: cartaoDebito ? 'debito' : 'credito',
        bandeira: formData.bandeiraCartao,
        parcelas: cartaoDebito ? 1 : formData.parcelasCartao,
      })
    : null;
  const percentualCartaoPreview = formData.cartaoAntecipado && taxaCartaoSelecionada?.taxaAntecipacaoPercentual != null
    ? taxaCartaoSelecionada.taxaAntecipacaoPercentual
    : taxaCartaoSelecionada?.taxaPercentual;
  const valorNumericoFormulario = (() => {
    let v = String(formData.valor).replace(/\./g, '').replace(',', '.');
    if (/^\d+$/.test(formData.valor)) v = parseInt(formData.valor, 10) / 100;
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  })();
  const valorTaxaCartaoPreview = taxaCartaoSelecionada && percentualCartaoPreview != null
    ? (valorNumericoFormulario * (parseFloat(percentualCartaoPreview) / 100))
    : null;

  const carregarDados = async () => {
    try {
      setLoading(true);
      const response = await listarFaturamento();
      const lista = response.data || [];
      setFaturamentos(lista);
      reconciliarNotasPendentes(lista);
    } catch (err) {
      setError('Erro ao carregar faturamentos');
    } finally {
      setLoading(false);
    }
  };

  // Reconsulta (uma vez cada, sem loop) qualquer nota que ficou "processando"
  // da última vez — cobre o caso de a página ter sido recarregada no meio do
  // processamento (o que derruba a re-consulta automática do handleEmitirNota
  // e deixava a nota pendurada em "Pendente" pra sempre, mesmo já autorizada
  // de verdade na prefeitura).
  const reconciliarNotasPendentes = async (lista) => {
    const pendentes = lista.filter((f) => f.statusNota === 'processando' && f.numeroNota);
    for (const f of pendentes) {
      try {
        const resultado = await statusNotaFiscal(f.id);
        if (resultado.statusNota && resultado.statusNota !== 'processando') {
          setFaturamentos((prev) => prev.map((item) => (
            item.id === f.id ? { ...item, notaEmitida: resultado.notaEmitida, statusNota: resultado.statusNota } : item
          )));
        }
      } catch (e) {
        console.error('Erro ao reconciliar nota pendente:', f.id, e);
      }
    }
  };

  const carregarPacientes = async () => {
    try {
      const data = await pacienteService.listar({ page: 1, limit: 9999 });
      const lista = Array.isArray(data) ? data : data?.pacientes || [];
      setPacientes(lista.filter(p => p.ativo));
    } catch (err) {
      console.error('Erro ao carregar pacientes:', err);
    }
  };

  const carregarProcedimentos = async () => {
    try {
      const data = await procedimentoService.listar();
      setProcedimentos(data.filter(p => p.ativo));
    } catch (err) {
      console.error('Erro ao carregar procedimentos:', err);
    }
  };

  const abrirModalNovo = () => {
    setModoEdicao(false);
    setItemEdicao(null);
    setPacienteSelecionado(null);
    setProcedimentoSelecionado(null);
    setPagadorSelecionado(null);
    setPagadorBeneficiario(true);
    setFormData({
      descricao: '',
      valor: '',
      data: new Date().toISOString().split('T')[0],
      paciente: '',
      cpf: '',
      formaPagamento: '',
      tipoPessoa: 'PF',
      observacoes: '',
      declarar: true,
      pagadorNome: '',
      pagadorCpf: '',
      pagadorTipoPessoa: 'PF',
      pagadorCep: '',
      pagadorLogradouro: '',
      pagadorNumero: '',
      pagadorComplemento: '',
      pagadorBairro: '',
      pagadorCidade: '',
      pagadorEstado: '',
      maquinaCartaoId: '',
      parcelasCartao: '',
      bandeiraCartao: '',
      cartaoAntecipado: false,
      taxaCartaoResponsavel: 'clinica',
    });
    setOpenModal(true);
  };

  const abrirModalEdicao = async (item) => {
    setModoEdicao(true);
    setItemEdicao(item);

    // Buscar paciente pelo nome para preencher autocomplete
    const pacienteEncontrado = pacientes.find(p => p.nome === item.paciente);
    if (pacienteEncontrado) {
      setPacienteSelecionado(pacienteEncontrado);
    } else {
      setPacienteSelecionado(null);
    }

    // Buscar procedimento pelo nome para preencher autocomplete
    const procedimentoEncontrado = procedimentos.find(p => p.nome === item.descricao);
    if (procedimentoEncontrado) {
      setProcedimentoSelecionado(procedimentoEncontrado);
    } else {
      setProcedimentoSelecionado(null);
    }

    // Verificar se tem pagador diferente
    const temPagadorDiferente = item.pagadorNome && item.pagadorNome !== '';
    setPagadorBeneficiario(!temPagadorDiferente);
    const pagadorEncontrado = temPagadorDiferente ? pacientes.find(p => p.nome === item.pagadorNome) : null;
    setPagadorSelecionado(pagadorEncontrado || null);

    setFormData({
      descricao: item.descricao || '',
      valor: item.valor ? formatarValorInput(String(item.valor)) : '',
      data: item.data || '',
      paciente: item.paciente || '',
      cpf: item.cpf || '',
      formaPagamento: item.formaPagamento || '',
      tipoPessoa: item.tipoPessoa || 'PF',
      observacoes: item.observacoes || '',
      declarar: item.declarar !== undefined ? Boolean(item.declarar) : true,
      pagadorNome: item.pagadorNome || '',
      pagadorCpf: item.pagadorCpf || '',
      pagadorTipoPessoa: item.pagadorTipoPessoa || 'PF',
      pagadorCep: item.pagadorCep || '',
      pagadorLogradouro: item.pagadorLogradouro || '',
      pagadorNumero: item.pagadorNumero || '',
      pagadorComplemento: item.pagadorComplemento || '',
      pagadorBairro: item.pagadorBairro || '',
      pagadorCidade: item.pagadorCidade || '',
      pagadorEstado: item.pagadorEstado || '',
      maquinaCartaoId: item.maquinaCartaoId || '',
      parcelasCartao: item.parcelasCartao || '',
      bandeiraCartao: item.bandeiraCartao || '',
      cartaoAntecipado: !!item.cartaoAntecipado,
      taxaCartaoResponsavel: item.taxaCartaoResponsavel || 'clinica',
    });
    setOpenModal(true);
  };

  const fecharModal = () => {
    setOpenModal(false);
    setError('');
  };

  // Busca endereço pelo CEP (ViaCEP) quando o pagador é digitado manualmente
  // (não está cadastrado como paciente no sistema).
  const handlePagadorCepChange = async (valor) => {
    const numeros = valor.replace(/\D/g, '').slice(0, 8);
    setFormData((prev) => ({ ...prev, pagadorCep: numeros }));
    if (numeros.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${numeros}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData((prev) => ({
            ...prev,
            pagadorLogradouro: data.logradouro || '',
            pagadorBairro: data.bairro || '',
            pagadorCidade: data.localidade || '',
            pagadorEstado: data.uf || '',
            pagadorCep: numeros,
          }));
        }
      } catch (err) {
        // Silencia erro de busca de CEP
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      setLoading(true);
      let tipoPessoaFinal = formData.tipoPessoa;
      if (!isHibrido) {
        tipoPessoaFinal = tipoUsuario;
      }
      // Corrige a conversão do valor: remove pontos de milhar, troca vírgula por ponto
      let valorStr = String(formData.valor).replace(/\./g, '').replace(',', '.');
      // Se o usuário digitou só números (ex: '150'), transforma para '150.00'
      if (/^\d+$/.test(formData.valor)) {
        valorStr = parseInt(formData.valor, 10) / 100;
      }
      const valorConvertido = parseFloat(valorStr);

      const dados = {
        descricao: formData.descricao,
        valor: isNaN(valorConvertido) ? 0 : valorConvertido,
        data: formData.data,
        paciente: formData.paciente,
        cpf: formData.cpf,
        pacienteId: pacienteSelecionado?.id || null,
        formaPagamento: formData.formaPagamento,
        tipoPessoa: tipoPessoaFinal,
        observacoes: formData.observacoes,
        pagadorNome: pagadorBeneficiario ? '' : formData.pagadorNome,
        pagadorCpf: pagadorBeneficiario ? '' : formData.pagadorCpf,
        pagadorTipoPessoa: pagadorBeneficiario ? null : formData.pagadorTipoPessoa,
        pagadorCep: pagadorBeneficiario ? '' : formData.pagadorCep,
        pagadorLogradouro: pagadorBeneficiario ? '' : formData.pagadorLogradouro,
        pagadorNumero: pagadorBeneficiario ? '' : formData.pagadorNumero,
        pagadorComplemento: pagadorBeneficiario ? '' : formData.pagadorComplemento,
        pagadorBairro: pagadorBeneficiario ? '' : formData.pagadorBairro,
        pagadorCidade: pagadorBeneficiario ? '' : formData.pagadorCidade,
        pagadorEstado: pagadorBeneficiario ? '' : formData.pagadorEstado,
        declarar: formData.declarar,
        ...(((formData.formaPagamento === 'Cartão de Crédito' && formData.parcelasCartao) || cartaoDebito) && formData.maquinaCartaoId && {
          maquinaCartaoId: formData.maquinaCartaoId,
          parcelasCartao: cartaoDebito ? 1 : formData.parcelasCartao,
          bandeiraCartao: formData.bandeiraCartao,
          cartaoAntecipado: cartaoDebito ? false : formData.cartaoAntecipado,
          taxaCartaoResponsavel: formData.taxaCartaoResponsavel,
        }),
      };
  // ...removido debug...
      // Tenta criar o faturamento, ignorando erro de paciente não cadastrado
      let erroPaciente = false;
      try {
        if (modoEdicao) {
          await atualizarFaturamento(itemEdicao.id, dados);
          setSuccess('Faturamento atualizado!');
        } else {
          await criarFaturamento(dados);
          setSuccess('Faturamento criado!');
        }
      } catch (errInterno) {
        const msgInterno = errInterno.response?.data?.message || '';
        if (msgInterno.toLowerCase().includes('paciente') && msgInterno.toLowerCase().includes('não encontrado')) {
          erroPaciente = true;
          setSuccess('Lançamento realizado sem paciente cadastrado!');
        } else {
          throw errInterno;
        }
      }
      fecharModal();
      carregarDados();
      setTimeout(() => setSuccess(''), 3000);
      // Após lançamento, sugere cadastro se não existir
      if (erroPaciente) {
        setTimeout(() => {
          if (window.confirm('Paciente não cadastrado. Deseja cadastrar agora para facilitar futuros lançamentos?')) {
            window.open('/pacientes', '_blank');
          }
        }, 500);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao salvar';
      setError(msg);
      alert('Erro ao salvar faturamento! Veja o corpo da requisição no console para debug.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletar = (item) => {
    setItemExcluir(item);
    setOpenExcluirModal(true);
  };

  const confirmarExclusao = async () => {
    if (!itemExcluir) return;
    try {
      setLoading(true);
      await deletarFaturamento(itemExcluir.id);
      setSuccess('Faturamento excluído!');
      setOpenExcluirModal(false);
      setItemExcluir(null);
      carregarDados();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao excluir');
    } finally {
      setLoading(false);
    }
  };

  const formatarData = (data) => {
    return new Date(data + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const formatarMoeda = (valor) => {
    return parseFloat(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatarCPF = (valor) => {
    // Remove tudo que não é dígito
    const numeros = valor.replace(/\D/g, '');
    // Formata: 000.000.000-00
    return numeros
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      .slice(0, 14); // Limita a 14 caracteres
  };

  const formatarValorInput = (valor) => {
    // Remove tudo que não é dígito, exceto vírgula
    let v = valor.replace(/[^\d,]/g, '');
    // Se já tem vírgula, limita a 2 casas decimais
    if (v.includes(',')) {
      const [inteiro, decimal] = v.split(',');
      return inteiro + ',' + (decimal || '').slice(0, 2);
    }
    // Se não tem vírgula, insere automaticamente ao digitar
    if (v.length === 0) return '';
    if (v.length === 1) return '0,0' + v;
    if (v.length === 2) return '0,' + v;
    return v.slice(0, v.length - 2) + ',' + v.slice(-2);
  };

  // Cálculos
  const totalFaturamento = faturamentos.reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);
  const totalPF = faturamentos.filter(f => f.tipoPessoa === 'PF').reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);
  const totalPJ = faturamentos.filter(f => f.tipoPessoa === 'PJ').reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);
  const impostosPF = totalPF * 0.275;
  const impostosPJ = totalPJ * 0.15;
  const totalImpostos = impostosPF + impostosPJ;
  const lucroLiquido = totalFaturamento - totalImpostos;
  const notasEmitidas = faturamentos.filter(f => f.notaEmitida).length;
  const notasPendentes = faturamentos.filter(f => f.tipoPessoa === 'PJ' && !f.notaEmitida).length;

  // Funções de Nota
  const abrirModalNota = (item) => {
    setItemSelecionado(item);
    setEmailPaciente('');
    setOpenNotaModal(true);
  };

  const fecharModalNota = () => {
    setOpenNotaModal(false);
    setItemSelecionado(null);
    setEmailPaciente('');
  };

  const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Reconsulta o status de uma nota que ficou "processando" até resolver
  // (autorizado/erro) ou desistir depois de algumas tentativas — evita que o
  // dentista precise atualizar a página manualmente pra ver o recibo.
  const reconsultarStatusNota = async (faturamentoId, tentativas = 5) => {
    for (let i = 0; i < tentativas; i += 1) {
      await esperar(3000);
      try {
        const resultado = await statusNotaFiscal(faturamentoId);
        if (resultado.statusNota === 'autorizado') {
          setSuccess('Nota Fiscal autorizada pela prefeitura!');
          setItemSelecionado(prev => (prev?.id === faturamentoId ? { ...prev, notaEmitida: true } : prev));
          carregarDados();
          setTimeout(() => { setSuccess(''); setError(''); }, 3000);
          return;
        }
        if (resultado.statusNota === 'erro') {
          setError(`Nota rejeitada pela prefeitura: ${resultado.erroNota || 'erro não especificado'}`);
          setSuccess('');
          carregarDados();
          setTimeout(() => { setSuccess(''); setError(''); }, 5000);
          return;
        }
      } catch (e) {
        console.error('Erro ao reconsultar status da nota:', e);
      }
    }
    // Não resolveu depois de todas as tentativas — avisa e deixa carregarDados()
    // pegar o estado real na próxima vez que a lista for atualizada.
    setSuccess('');
    setError('A prefeitura está demorando mais que o normal. Atualize a página em alguns instantes pra ver o resultado.');
    setTimeout(() => setError(''), 6000);
  };

  const handleEmitirNota = async () => {
    // Validação prévia: CPF/CNPJ do beneficiário é obrigatório para emissão
    const cpfBeneficiario = itemSelecionado?.cpf || itemSelecionado?.pagador_cpf || '';
    const cnpjBeneficiario = itemSelecionado?.cnpj || itemSelecionado?.pagador_cnpj || '';
    const temDocumento = cpfBeneficiario.replace(/\D/g, '').length >= 11 || cnpjBeneficiario.replace(/\D/g, '').length >= 14;
    if (!temDocumento) {
      setError('O paciente não possui CPF/CNPJ cadastrado. Acesse o cadastro do paciente, preencha o documento e tente novamente.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      console.log('🟡 [EmitirNota] Chamando API para id:', itemSelecionado.id);
      const response = await emitirNotaFiscal(itemSelecionado.id);
      // O interceptor do axios já extrai response.data, então 'response' é o JSON do backend direto
      console.log('📦 [EmitirNota] response:', JSON.stringify(response, null, 2));
      console.log('📦 [EmitirNota] success?', response?.success);

      if (response.success && !response.aindaProcessando) {
        console.log('✅ [EmitirNota] Autorizada!');
        setSuccess('Nota Fiscal emitida com sucesso!');
        setItemSelecionado(prev => ({ ...prev, notaEmitida: true, numeroNota: response.data?.ref || response.data?.id || prev.numeroNota }));
        carregarDados();
        setTimeout(() => { setSuccess(''); setError(''); }, 3000);
      } else if (response.success && response.aindaProcessando) {
        // A prefeitura ainda não confirmou — não marca como emitida ainda, senão o
        // dentista acha que já saiu (recibo não aparece) e clica em emitir de novo.
        console.log('⏳ [EmitirNota] Ainda processando, reconsultando...');
        setSuccess('Nota enviada! A prefeitura ainda está processando, isso pode levar alguns segundos...');
        reconsultarStatusNota(itemSelecionado.id);
      } else {
        console.error('❌ [EmitirNota] success=false:', response.message);
        setError(response.message || 'Erro ao emitir nota fiscal');
        setTimeout(() => { setSuccess(''); setError(''); }, 3000);
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 422 && data?.camposFaltando) {
        // Dados da clínica incompletos — redireciona para perfil
        fecharModalNota();
        setAlertClinica({ open: true, campos: data.camposFaltando });
      } else {
        const detalhe = data?.error?.mensagem || data?.error?.message
          || (typeof data?.error === 'string' ? data.error : null)
          || data?.message || err?.message || 'Erro ao emitir nota fiscal';
        console.error('❌ Erro NFS-e message:', data?.message);
        console.error('❌ Erro NFS-e error:', data?.error?.message || data?.error?.mensagem || data?.error?.errors || data?.error);
        setError(detalhe);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelarNota = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar esta Nota Fiscal? Esta ação não pode ser desfeita.')) return;
    try {
      setLoading(true);
      setError('');
      const api = (await import('../../services/api')).default;
      await api.post(`/faturamento/${itemSelecionado.id}/cancelar-nota`, { motivo: 'Cancelamento solicitado pelo prestador.' });
      setSuccess('Nota Fiscal cancelada com sucesso!');
      setItemSelecionado(prev => ({ ...prev, notaEmitida: false, numeroNota: null }));
      carregarDados();
    } catch (err) {
      setError(err?.message || 'Erro ao cancelar nota fiscal');
    } finally {
      setLoading(false);
      setTimeout(() => { setSuccess(''); setError(''); }, 3000);
    }
  };

  const handleBaixarDocumento = async () => {
    try {
      setLoading(true);

      // Nota migrada: tem URL S3 direta, abre sem passar pelo backend
      if (itemSelecionado.notaFiscalUrl && !itemSelecionado.numeroNota) {
        const link = document.createElement('a');
        link.href = itemSelecionado.notaFiscalUrl;
        link.setAttribute('download', itemSelecionado.notaFiscalUrl.split('/').pop() || 'nota-fiscal.pdf');
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }

      const token = localStorage.getItem('token');
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${baseURL}/faturamento/${itemSelecionado.id}/baixar-nota`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Erro ao baixar PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nota-fiscal-${itemSelecionado.numeroNota || itemSelecionado.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erro ao baixar PDF da nota fiscal');
      console.error('Erro download PDF:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnviarEmail = () => {
    if (!emailPaciente) {
      setError('Digite o email do paciente');
      return;
    }
    const tipo = itemSelecionado.tipoPessoa === 'PJ' ? 'Nota Fiscal' : 'Recibo';
    alert(`📧 Enviando ${tipo} para: ${emailPaciente}`);
    setSuccess(`${tipo} enviado(a)!`);
    setTimeout(() => setSuccess(''), 3000);
    fecharModalNota();
  };

  // ...existing code...

  // Filtrar faturamentos por mês/ano E pesquisa
  const faturamentosFiltrados = faturamentos.filter(f => {
    const dataFaturamento = new Date(f.data + 'T00:00:00');
    const passaMesAno = dataFaturamento.getMonth() + 1 === mesFiltro && dataFaturamento.getFullYear() === anoFiltro;

    // Filtro de pesquisa (nome, descrição ou CPF)
    if (pesquisa) {
      const termoPesquisa = pesquisa.toLowerCase();
      const nomeMatch = f.paciente?.toLowerCase().includes(termoPesquisa);
      const descricaoMatch = f.descricao?.toLowerCase().includes(termoPesquisa);
      const cpfMatch = f.cpf?.replace(/\D/g, '').includes(termoPesquisa.replace(/\D/g, ''));
      return passaMesAno && (nomeMatch || descricaoMatch || cpfMatch);
    }

    return passaMesAno;
  });

  const faturamentosOrdenados = [...faturamentosFiltrados].sort((a, b) => new Date(b.data) - new Date(a.data));
  const totalPaginas = Math.ceil(faturamentosOrdenados.length / LIMITE_PAGINA);
  const faturamentosPaginados = faturamentosOrdenados.slice((paginaFaturamento - 1) * LIMITE_PAGINA, paginaFaturamento * LIMITE_PAGINA);

  // Recalcular totais com dados filtrados
  const totalFaturamentoFiltrado = faturamentosFiltrados.reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);
  const totalDeclaradoFiltrado = faturamentosFiltrados.filter(f => f.declarar !== false && f.declarar !== 0).reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);
  const totalNaoDeclaradoFiltrado = faturamentosFiltrados.filter(f => f.declarar === false || f.declarar === 0).reduce((acc, item) => acc + parseFloat(item.valor || 0), 0);
  const notasEmitidasFiltradas = faturamentosFiltrados.filter(f => f.notaEmitida).length;
  const notasPendentesFiltradas = faturamentosFiltrados.filter(f => f.tipoPessoa === 'PJ' && !f.notaEmitida).length;

  const meses = [
    { valor: 1, nome: 'Janeiro' }, { valor: 2, nome: 'Fevereiro' }, { valor: 3, nome: 'Março' },
    { valor: 4, nome: 'Abril' }, { valor: 5, nome: 'Maio' }, { valor: 6, nome: 'Junho' },
    { valor: 7, nome: 'Julho' }, { valor: 8, nome: 'Agosto' }, { valor: 9, nome: 'Setembro' },
    { valor: 10, nome: 'Outubro' }, { valor: 11, nome: 'Novembro' }, { valor: 12, nome: 'Dezembro' }
  ];

  const anos = [2023, 2024, 2025, 2026];

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: 'auto', md: 'calc(100vh - 100px)' }, gap: 2, backgroundColor: isDarkMode ? undefined : '#FFFAF5' }}>
      {/* ===== PAINEL LATERAL ESQUERDO ===== */}
      <Paper sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, borderTop: '3px solid #F97316', background: isDarkMode ? undefined : '#FFF7ED' }}>
        <Typography variant="h6" fontWeight={600} sx={{ borderLeft: '4px solid #F97316', pl: 1.5 }}>Faturamento</Typography>

        {/* Filtro de Mês/Ano */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Mês</InputLabel>
            <Select
              value={mesFiltro}
              label="Mês"
              onChange={(e) => { setMesFiltro(e.target.value); setPaginaFaturamento(1); }}
            >
              {meses.map(mes => (
                <MenuItem key={mes.valor} value={mes.valor}>{mes.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Ano</InputLabel>
            <Select
              value={anoFiltro}
              label="Ano"
              onChange={(e) => { setAnoFiltro(e.target.value); setPaginaFaturamento(1); }}
            >
              {anos.map(ano => (
                <MenuItem key={ano} value={ano}>{ano}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ borderColor: 'rgba(249,115,22,0.2)' }} />

        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '0.68em' }}>Faturamento Bruto</Typography>
            <Typography variant="h5" fontWeight={600} color="success.main">
              {formatarMoeda(totalFaturamentoFiltrado)}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(249,115,22,0.2)' }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Declarado</Typography>
              <Typography variant="body2" fontWeight={500} color="success.main">
                {formatarMoeda(totalDeclaradoFiltrado)}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">Controle interno</Typography>
              <Typography variant="body2" fontWeight={500} sx={{ color: '#6B7280' }}>
                {formatarMoeda(totalNaoDeclaradoFiltrado)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(249,115,22,0.2)' }} />

          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>Documentos Fiscais</Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip label={`${notasEmitidasFiltradas} ok`} size="small" color="success" variant="outlined" />
              {notasPendentesFiltradas > 0 && <Chip label={`${notasPendentesFiltradas} pend`} size="small" color="warning" variant="outlined" />}
            </Box>
          </Box>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(249,115,22,0.2)' }} />

          <Box>
            <Typography variant="caption" color="text.secondary">Lançamentos no período</Typography>
            <Typography variant="h6" fontWeight={500}>{faturamentosFiltrados.length}</Typography>
          </Box>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={abrirModalNovo}
          fullWidth
          disabled={atingiuLimiteFree}
          sx={{
            mt: 'auto',
            bgcolor: '#F97316',
            fontWeight: 700,
            letterSpacing: '0.5px',
            boxShadow: '0 2px 10px rgba(249,115,22,0.35)',
            '&:hover': { bgcolor: '#EA580C', boxShadow: '0 4px 14px rgba(249,115,22,0.45)' },
            '&:disabled': { bgcolor: 'rgba(249,115,22,0.3)' },
          }}
        >
          Novo Lançamento
        </Button>
      </Paper>

      {/* ===== ÁREA PRINCIPAL - LISTA ===== */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Barra de Pesquisa */}
        <Paper sx={{ p: 2, mb: 2, borderTop: '2px solid rgba(249,115,22,0.3)' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Pesquisar por paciente ou CPF..."
            value={pesquisa}
            onChange={(e) => { setPesquisa(e.target.value); setPaginaFaturamento(1); }}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                  <SearchIcon fontSize="small" />
                </Box>
              ),
            }}
          />
        </Paper>

        <Paper sx={{ flex: 1, overflow: 'auto', p: 0, borderTop: '2px solid rgba(249,115,22,0.25)' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress />
            </Box>
          ) : faturamentos.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={5}>
              <Typography color="text.secondary" gutterBottom>Nenhum faturamento cadastrado</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={abrirModalNovo}>
    Adicionar primeiro lançamento
    disabled={atingiuLimiteFree}
              </Button>
            </Box>
          ) : (
            <Box>
              {faturamentosPaginados
                .map((faturamento) => {
                  const isControle = faturamento.declarar === false || faturamento.declarar === 0;
                  return (
                    <Box
                      key={faturamento.id}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: { xs: 1, sm: 0 },
                        p: 2,
                        borderBottom: '1px solid rgba(249,115,22,0.12)',
                        '&:hover': { bgcolor: isControle ? 'rgba(107,114,128,0.06)' : 'rgba(249,115,22,0.04)' },
                        opacity: isControle ? 0.65 : 1,
                        bgcolor: isControle ? 'rgba(107,114,128,0.04)' : 'transparent',
                      }}
                    >
                      {/* Data */}
                      <Box display="flex" alignItems="center" gap={2} flex={1} sx={{ minWidth: 0 }}>
                        <Box sx={{ minWidth: 50, textAlign: 'center' }}>
                          <Typography variant="body2" fontWeight={500} color="text.secondary">
                            {new Date(faturamento.data + 'T00:00:00').getDate().toString().padStart(2, '0')}/{new Date(faturamento.data + 'T00:00:00').toLocaleDateString('pt-BR', { month: '2-digit' })}
                          </Typography>
                        </Box>

                        {/* Info */}
                        <Box flex={1} sx={{ minWidth: 0 }}>
                          <Tooltip title={faturamento.cpf ? `CPF: ${faturamento.cpf}` : 'CPF não informado'} arrow>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: { xs: 170, sm: 260 } }}>
                                {faturamento.paciente || (faturamento.descricao?.match(/prestados a (.+)/i)?.[1]) || '—'}
                              </Typography>
                              <Chip
                                label={faturamento.tipoPessoa}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20, fontSize: '0.7rem', fontWeight: 600,
                                  borderColor: faturamento.tipoPessoa === 'PJ' ? '#F97316' : '#3B82F6',
                                  color: faturamento.tipoPessoa === 'PJ' ? '#EA580C' : '#2563EB',
                                }}
                              />
                              {faturamento.pagadorNome && (
                                <Tooltip title={`Pagador: ${faturamento.pagadorNome}`} arrow>
                                  <Chip
                                    icon={<SwapHorizIcon />}
                                    label="Pagador diferente"
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary">
                            {faturamento.descricao}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Valor */}
                      <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 3 }} sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
                        <Box textAlign="right" sx={{ minWidth: 100 }}>
                          <Typography variant="body1" fontWeight={600} color="success.main">
                            {formatarMoeda(faturamento.valor)}
                          </Typography>
                        </Box>

                        {/* Status */}
                        <Box sx={{ minWidth: 80, textAlign: 'center' }}>
                          {isControle ? (
                            <Chip label="Controle" size="small" sx={{ fontSize: '0.7rem', bgcolor: '#6B7280', color: '#fff' }} />
                          ) : faturamento.tipoPessoa === 'PF' ? (
                            faturamento.reciboNome ? (
                              <Chip label="Recibo OK" size="small" color="success" icon={<CheckCircleIcon />} sx={{ fontSize: '0.7rem' }} />
                            ) : (
                              <Chip label="Pendente" size="small" variant="outlined" color="warning" sx={{ fontSize: '0.7rem' }} />
                            )
                          ) : (
                            faturamento.notaEmitida ? (
                              <Chip label="Emitida" size="small" color="success" icon={<CheckCircleIcon />} sx={{ fontSize: '0.7rem' }} />
                            ) : (
                              <Chip label="Pendente" size="small" variant="outlined" color="warning" sx={{ fontSize: '0.7rem' }} />
                            )
                          )}
                        </Box>

                        {/* Ações */}
                        <Box display="flex" gap={0.5}>
                          {!isControle && (faturamento.tipoPessoa === 'PF' ? (
                            faturamento.reciboNome ? (
                              <Tooltip title="Baixar recibo">
                                <IconButton size="small" onClick={() => downloadReciboPF(faturamento.id)} color="success">
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Aguardando recibo do admin">
                                <span>
                                  <IconButton size="small" disabled>
                                    <DescriptionIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )
                          ) : (
                            <Tooltip title={faturamento.notaEmitida ? "Baixar/Enviar" : "Emitir"}>
                              <IconButton size="small" onClick={() => abrirModalNota(faturamento)} color={faturamento.notaEmitida ? "success" : "primary"}>
                                {faturamento.notaEmitida ? <DownloadIcon fontSize="small" /> : <DescriptionIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          ))}
                          {!faturamento.reciboNome && !faturamento.notaEmitida && (
                            <Tooltip title="Editar">
                              <IconButton size="small" onClick={() => abrirModalEdicao(faturamento)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!faturamento.reciboNome && !faturamento.notaEmitida && (
                            <Tooltip title="Excluir">
                              <IconButton size="small" onClick={() => handleDeletar(faturamento)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO */}
                          <Dialog open={openExcluirModal} onClose={() => setOpenExcluirModal(false)} maxWidth="xs" fullWidth>
                            <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}>
                              <Typography variant="h6" fontWeight={600}>Confirmar exclusão</Typography>
                            </DialogTitle>
                            <DialogContent sx={{ pt: 2.5, pb: 2 }}>
                              <Typography variant="body1" gutterBottom>
                                Tem certeza que deseja excluir este lançamento?
                              </Typography>
                              {itemExcluir && (
                                <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mt: 1 }}>
                                  <Typography variant="subtitle2">{itemExcluir.descricao}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatarData(itemExcluir.data)} • {itemExcluir.paciente} • {formatarMoeda(itemExcluir.valor)}
                                  </Typography>
                                </Box>
                              )}
                            </DialogContent>
                            <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                              <Button onClick={() => setOpenExcluirModal(false)} size="small">
                                Cancelar
                              </Button>
                              <Button
                                onClick={confirmarExclusao}
                                variant="contained"
                                color="error"
                                size="small"
                                disabled={loading}
                              >
                                Excluir
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              {totalPaginas > 1 && (
                <Box display="flex" justifyContent="center" py={2}>
                  <Pagination
                    count={totalPaginas}
                    page={paginaFaturamento}
                    onChange={(_, p) => setPaginaFaturamento(p)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Box>
      <Dialog open={openModal} onClose={fecharModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', pb: 1.5 }}>
          <Typography variant="h6" fontWeight="600">
            {modoEdicao ? 'Editar receita' : 'Nova receita'}
          </Typography>
          <IconButton
            onClick={fecharModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2.5, pb: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Grid container spacing={2}>
              {/* Linha 1: Valor e Data */}
              <Grid item xs={6}>
                <TextField
                  label="Valor *"
                  fullWidth
                  size="small"
                  value={formData.valor}
                  onChange={(e) => {
                    // Permite apenas números, limita a 9 dígitos (até 9.999.999,99)
                    let valorDigitado = e.target.value.replace(/\D/g, '').replace(/^0+/, '');
                    valorDigitado = valorDigitado.slice(0, 9);
                    // Aplica máscara de reais em tempo real
                    let valorFormatado = '';
                    if (valorDigitado.length === 0) {
                      valorFormatado = '';
                    } else if (valorDigitado.length === 1) {
                      valorFormatado = '0,0' + valorDigitado;
                    } else if (valorDigitado.length === 2) {
                      valorFormatado = '0,' + valorDigitado;
                    } else {
                      // Remove zeros à esquerda do inteiro
                      let inteiro = valorDigitado.slice(0, valorDigitado.length - 2).replace(/^0+/, '');
                      if (inteiro === '') inteiro = '0';
                      let inteiroFormatado = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                      valorFormatado = inteiroFormatado + ',' + valorDigitado.slice(-2);
                    }
                    setFormData({ ...formData, valor: valorFormatado });
                  }}
                  error={formData.valor === '' || isNaN(parseFloat(String(formData.valor).replace('.', '').replace(',', '.'))) || parseFloat(String(formData.valor).replace('.', '').replace(',', '.')) <= 0}
                  placeholder="0,00"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Data *"
                  fullWidth
                  size="small"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.data}
                  onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                />
              </Grid>

              {/* Linha 2: Procedimento */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={procedimentos}
                    getOptionLabel={(option) => option.nome || ''}
                    value={procedimentoSelecionado}
                    freeSolo
                    onChange={(e, newValue) => {
                      if (typeof newValue === 'string') {
                        setFormData({ ...formData, descricao: newValue });
                        setProcedimentoSelecionado(null);
                      } else if (newValue) {
                        setProcedimentoSelecionado(newValue);
                        // Formata valorPadrao para o formato de input (ex: '150,00')
                        let valorPadrao = newValue.valorPadrao;
                        let valorFormatado = formData.valor;
                        if (valorPadrao !== undefined && valorPadrao !== null && valorPadrao !== '') {
                          // Garante que é número
                          let valorNum = typeof valorPadrao === 'string' ? parseFloat(valorPadrao) : valorPadrao;
                          valorFormatado = valorNum.toFixed(2).replace('.', ',');
                        }
                        setFormData({
                          ...formData,
                          descricao: newValue.nome,
                          valor: valorFormatado
                        });
                      }
                    }}
                    onInputChange={(e, newValue) => {
                      setFormData({ ...formData, descricao: newValue });
                    }}
                    inputValue={formData.descricao}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Procedimento *"
                        required
                        placeholder="Digite ou selecione"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2">{option.nome}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.categoria}{option.valorPadrao && ` • R$ ${parseFloat(option.valorPadrao).toFixed(2)}`}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    noOptionsText={
                      <Box sx={{ py: 1, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Nenhum procedimento cadastrado</Typography>
                        <Button
                          size="small"
                          startIcon={<MedicalServicesIcon />}
                          onClick={() => window.open('/procedimentos', '_blank')}
                          sx={{ mt: 1 }}
                        >
                          Cadastrar procedimento
                        </Button>
                      </Box>
                    }
                  />
                  <Tooltip
                    title="Clique para cadastrar procedimentos antecipadamente. Isso facilita futuros lançamentos, mantém histórico e garante padronização dos dados."
                    arrow
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={() => window.open('/procedimentos', '_blank')}
                      sx={{
                        mt: 0.5,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      <MedicalServicesIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>

              {/* Linha 3: Paciente e CPF */}
              <Grid item xs={8}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={pacientes}
                    getOptionLabel={(option) => option.nome || ''}
                    value={pacienteSelecionado}
                    freeSolo
                    onChange={(e, newValue) => {
                      if (typeof newValue === 'string') {
                        setFormData({ ...formData, paciente: newValue, cpf: '' });
                        setPacienteSelecionado(null);
                      } else if (newValue) {
                        setPacienteSelecionado(newValue);
                        setFormData({
                          ...formData,
                          paciente: newValue.nome,
                          cpf: newValue.cpfCnpj || newValue.cpf_cnpj || newValue.cpf || '',
                          // Paciente não tem mais tipoPessoa próprio (não fazia sentido — quem
                          // decide PF/PJ é o faturamento). Se a clínica é híbrida, o usuário
                          // escolhe manualmente; senão, segue o tipo fixo da clínica.
                          tipoPessoa: isHibrido ? 'PF' : tipoUsuario
                        });
                      }
                    }}
                    onInputChange={(e, newValue) => {
                      if (e && e.type === 'change') {
                        setFormData({ ...formData, paciente: newValue, cpf: '' });
                        setPacienteSelecionado(null);
                      }
                    }}
                    inputValue={formData.paciente}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Paciente *"
                        required
                        placeholder="Digite ou selecione"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2">{option.nome}</Typography>
                          {(option.cpfCnpj || option.cpf_cnpj || option.cpf) && (
                            <Typography variant="caption" color="text.secondary">{option.cpfCnpj || option.cpf_cnpj || option.cpf}</Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    noOptionsText={
                      <Box sx={{ py: 1, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Nenhum paciente cadastrado</Typography>
                        <Button
                          size="small"
                          startIcon={<PersonAddIcon />}
                          onClick={() => window.open('/pacientes', '_blank')}
                          sx={{ mt: 1 }}
                        >
                          Cadastrar paciente
                        </Button>
                      </Box>
                    }
                  />
                  <Tooltip
                    title="Clique para cadastrar pacientes antecipadamente. Isso facilita futuros lançamentos, mantém histórico completo e garante padronização dos dados fiscais."
                    arrow
                    placement="top"
                  >
                    <IconButton
                      size="small"
                      onClick={() => window.open('/pacientes', '_blank')}
                      sx={{
                        mt: 0.5,
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      <PersonAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="CPF"
                  fullWidth
                  size="small"
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: formatarCPF(e.target.value) })}
                  placeholder="000.000.000-00"
                  inputProps={{ maxLength: 14 }}
                  disabled={!!pacienteSelecionado}
                />
              </Grid>

              {/* Linha 4: Tipo de Pessoa só aparece para híbrido */}
              {isHibrido && (
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Pessoa</InputLabel>
                    <Select
                      value={formData.tipoPessoa}
                      label="Tipo de Pessoa"
                      onChange={(e) => setFormData({ ...formData, tipoPessoa: e.target.value })}
                      required
                    >
                      <MenuItem value="PF">Pessoa Física</MenuItem>
                      <MenuItem value="PJ">Pessoa Jurídica</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={isHibrido ? 6 : 12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Forma de Pagamento</InputLabel>
                  <Select
                    value={formData.formaPagamento}
                    label="Forma de Pagamento"
                    onChange={(e) => setFormData({ ...formData, formaPagamento: e.target.value })}
                  >
                    {formasPagamento.map((forma) => (
                      <MenuItem key={forma} value={forma}>{forma}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Taxa de máquina de cartão — só aparece com Cartão de Crédito ou Débito */}
              {(formData.formaPagamento === 'Cartão de Crédito' || cartaoDebito) && (
                <Grid item xs={12}>
                  <Box sx={{
                    border: '1px dashed rgba(249,115,22,0.4)', borderRadius: 2, p: 1.5,
                    display: 'flex', flexDirection: 'column', gap: 1.25,
                    bgcolor: isDarkMode ? 'rgba(249,115,22,0.06)' : 'rgba(249,115,22,0.04)',
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#F97316' }}>
                        <CreditCardIcon sx={{ fontSize: 15 }} /> Taxa da máquina (opcional)
                      </Typography>
                      <Button size="small" onClick={() => navigate('/maquinas-cartao')}
                        sx={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, py: 0.2 }}>
                        Gerenciar máquinas
                      </Button>
                    </Box>

                    {maquinasCartao.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Nenhuma máquina cadastrada ainda — o lançamento funciona normalmente sem isso.
                        Clique em "Gerenciar máquinas" pra cadastrar e calcular a taxa automaticamente.
                      </Typography>
                    ) : (
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Máquina</InputLabel>
                            <Select
                              value={formData.maquinaCartaoId}
                              label="Máquina"
                              onChange={(e) => setFormData({ ...formData, maquinaCartaoId: e.target.value, parcelasCartao: '', bandeiraCartao: '' })}
                            >
                              <MenuItem value=""><em>Nenhuma</em></MenuItem>
                              {maquinasCartao.map(m => (
                                <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl fullWidth size="small" disabled={!maquinaCartaoSelecionada}>
                            <InputLabel>Bandeira</InputLabel>
                            <Select
                              value={formData.bandeiraCartao}
                              label="Bandeira"
                              onChange={(e) => {
                                const nova = e.target.value;
                                const aindaExiste = parcelasDisponiveis(maquinaCartaoSelecionada, nova)
                                  .some(t => String(t.parcelas) === String(formData.parcelasCartao));
                                setFormData({ ...formData, bandeiraCartao: nova, parcelasCartao: aindaExiste ? formData.parcelasCartao : '' });
                              }}
                            >
                              <MenuItem value=""><em>Não informar (taxa padrão)</em></MenuItem>
                              {BANDEIRAS.map(b => (
                                <MenuItem key={b} value={b} sx={{ gap: 1.25 }}><BandeiraIcone bandeira={b} largura={28} />{b}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        {!cartaoDebito && (
                          <Grid item xs={12}>
                            <FormControl fullWidth size="small" disabled={!maquinaCartaoSelecionada}>
                              <InputLabel>Parcelas</InputLabel>
                              <Select
                                value={formData.parcelasCartao}
                                label="Parcelas"
                                onChange={(e) => setFormData({ ...formData, parcelasCartao: e.target.value })}
                              >
                                {parcelasDisponiveis(maquinaCartaoSelecionada, formData.bandeiraCartao).map(t => (
                                  <MenuItem key={t.parcelas} value={t.parcelas}>{t.parcelas}x ({t.taxaPercentual}%)</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        )}

                        {(cartaoDebito ? formData.maquinaCartaoId : formData.parcelasCartao) && (
                          <>
                            <Grid item xs={taxaCartaoSelecionada?.taxaAntecipacaoPercentual != null ? 6 : 12}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Taxa fica com</InputLabel>
                                <Select
                                  value={formData.taxaCartaoResponsavel}
                                  label="Taxa fica com"
                                  onChange={(e) => setFormData({ ...formData, taxaCartaoResponsavel: e.target.value })}
                                >
                                  <MenuItem value="clinica">Clínica (vira despesa dedutível)</MenuItem>
                                  <MenuItem value="paciente">Paciente</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                            {taxaCartaoSelecionada?.taxaAntecipacaoPercentual != null && (
                              <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox size="small" checked={formData.cartaoAntecipado}
                                      onChange={(e) => setFormData({ ...formData, cartaoAntecipado: e.target.checked })} />
                                  }
                                  label={
                                    <Typography variant="caption">
                                      Receber antecipado ({taxaCartaoSelecionada.taxaAntecipacaoPercentual}%)
                                    </Typography>
                                  }
                                />
                              </Grid>
                            )}
                            {valorTaxaCartaoPreview !== null && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                  Taxa estimada ({percentualCartaoPreview}%): R$ {valorTaxaCartaoPreview.toFixed(2).replace('.', ',')}
                                  {formData.taxaCartaoResponsavel === 'clinica'
                                    ? ' — será lançada automaticamente como despesa dedutível.'
                                    : ' — fica por conta do paciente.'}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Linha 5: Checkbox Pagador */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={pagadorBeneficiario}
                      size="small"
                      onChange={(e) => {
                        setPagadorBeneficiario(e.target.checked);
                        if (e.target.checked) {
                          setPagadorSelecionado(null);
                          setFormData({
                            ...formData,
                            pagadorNome: '',
                            pagadorCpf: '',
                            pagadorTipoPessoa: 'PF',
                            pagadorCep: '',
                            pagadorLogradouro: '',
                            pagadorNumero: '',
                            pagadorComplemento: '',
                            pagadorBairro: '',
                            pagadorCidade: '',
                            pagadorEstado: '',
                          });
                        }
                      }}
                    />
                  }
                  label={<Typography variant="body2">O pagador é o beneficiário?</Typography>}
                />
              </Grid>

              {/* Campos do Pagador (apenas se desmarcado) */}
              {!pagadorBeneficiario && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0 }}>
                      A nota fiscal sairá em nome do pagador, não do paciente (beneficiário).
                    </Alert>
                  </Grid>
                  <Grid item xs={6}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={pacientes}
                      getOptionLabel={(option) => option.nome || ''}
                      value={pagadorSelecionado}
                      freeSolo
                      onChange={(e, newValue) => {
                        if (typeof newValue === 'string') {
                          setFormData({
                            ...formData,
                            pagadorNome: newValue,
                            pagadorCpf: '',
                            pagadorCep: '',
                            pagadorLogradouro: '',
                            pagadorNumero: '',
                            pagadorComplemento: '',
                            pagadorBairro: '',
                            pagadorCidade: '',
                            pagadorEstado: '',
                          });
                          setPagadorSelecionado(null);
                        } else if (newValue) {
                          setPagadorSelecionado(newValue);
                          setFormData({
                            ...formData,
                            pagadorNome: newValue.nome,
                            pagadorCpf: newValue.cpfCnpj || newValue.cpf_cnpj || newValue.cpf || '',
                            pagadorTipoPessoa: 'PF',
                            pagadorCep: newValue.cep || '',
                            pagadorLogradouro: newValue.logradouro || '',
                            pagadorNumero: newValue.numero || '',
                            pagadorComplemento: newValue.complemento || '',
                            pagadorBairro: newValue.bairro || '',
                            pagadorCidade: newValue.cidade || '',
                            pagadorEstado: newValue.estado || '',
                          });
                        }
                      }}
                      onInputChange={(e, newValue) => {
                        if (e && e.type === 'change') {
                          setFormData({
                            ...formData,
                            pagadorNome: newValue,
                            pagadorCpf: '',
                            pagadorCep: '',
                            pagadorLogradouro: '',
                            pagadorNumero: '',
                            pagadorComplemento: '',
                            pagadorBairro: '',
                            pagadorCidade: '',
                            pagadorEstado: '',
                          });
                          setPagadorSelecionado(null);
                        }
                      }}
                      inputValue={formData.pagadorNome}
                      renderInput={(params) => (
                        <TextField {...params} label="Nome do Pagador *" required placeholder="Digite ou selecione" />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box>
                            <Typography variant="body2">{option.nome}</Typography>
                            {(option.cpfCnpj || option.cpf_cnpj || option.cpf) && (
                              <Typography variant="caption" color="text.secondary">{option.cpfCnpj || option.cpf_cnpj || option.cpf}</Typography>
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="CPF/CNPJ do Pagador *"
                      required
                      fullWidth
                      size="small"
                      value={formData.pagadorCpf}
                      onChange={(e) => setFormData({ ...formData, pagadorCpf: formatarCPF(e.target.value) })}
                      placeholder="000.000.000-00"
                      disabled={!!pagadorSelecionado}
                    />
                  </Grid>

                  {/* Endereço do pagador — obrigatório pra nota sair certinha.
                      Some sozinho quando um paciente é selecionado; digitável
                      quando o pagador não está cadastrado no sistema. */}
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="CEP"
                      fullWidth
                      size="small"
                      value={formData.pagadorCep}
                      onChange={(e) => handlePagadorCepChange(e.target.value)}
                      disabled={!!pagadorSelecionado}
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      label="Logradouro"
                      fullWidth
                      size="small"
                      value={formData.pagadorLogradouro}
                      onChange={(e) => setFormData({ ...formData, pagadorLogradouro: e.target.value })}
                      disabled={!!pagadorSelecionado}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="Número"
                      fullWidth
                      size="small"
                      value={formData.pagadorNumero}
                      onChange={(e) => setFormData({ ...formData, pagadorNumero: e.target.value })}
                      disabled={!!pagadorSelecionado}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Complemento"
                      fullWidth
                      size="small"
                      value={formData.pagadorComplemento}
                      onChange={(e) => setFormData({ ...formData, pagadorComplemento: e.target.value })}
                      disabled={!!pagadorSelecionado}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Bairro"
                      fullWidth
                      size="small"
                      value={formData.pagadorBairro}
                      onChange={(e) => setFormData({ ...formData, pagadorBairro: e.target.value })}
                      disabled={!!pagadorSelecionado}
                    />
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <TextField
                      label="Cidade"
                      fullWidth
                      size="small"
                      value={formData.pagadorCidade}
                      onChange={(e) => setFormData({ ...formData, pagadorCidade: e.target.value })}
                      disabled={!!pagadorSelecionado}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      label="UF"
                      fullWidth
                      size="small"
                      value={formData.pagadorEstado}
                      onChange={(e) => setFormData({ ...formData, pagadorEstado: e.target.value.toUpperCase() })}
                      inputProps={{ maxLength: 2 }}
                      disabled={!!pagadorSelecionado}
                    />
                  </Grid>
                  {!pagadorSelecionado && !formData.pagadorLogradouro && (
                    <Grid item xs={12}>
                      <Alert severity="warning" sx={{ py: 0 }}>
                        Sem endereço do pagador, a nota fiscal sairá sem endereço.
                      </Alert>
                    </Grid>
                  )}
                </>
              )}

              {/* Linha 6: Observações — também usada como descrição da nota fiscal, se preenchida */}
              <Grid item xs={12}>
                <TextField
                  label="Observações"
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  helperText="O campo de observação irá aparecer como descrição da nota fiscal."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.declarar}
                      onChange={(e) => setFormData({ ...formData, declarar: e.target.checked })}
                      size="small"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Declarar para imposto</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Se desmarcado, este lançamento aparece apenas como controle interno e não entra no cálculo de DARF, DAS ou RBT12.
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
            <Button onClick={fecharModal} size="small">
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={loading || !formData.valor || parseFloat(formData.valor) <= 0}
            >
              {loading ? 'Salvando...' : atingiuLimiteFree ? 'Limite do plano atingido' : 'Salvar'}
            </Button>
                {atingiuLimiteFree && (
                  <Alert severity="warning" sx={{ ml: 2, my: 1, fontSize: 13 }}>
                    Limite de 15 faturamentos/mês atingido no plano grátis.
                  </Alert>
                )}
          </DialogActions>
        </form>
      </Dialog>

      {/* ===== MODAL NOTA FISCAL/RECIBO ===== */}
      <Dialog open={openNotaModal} onClose={fecharModalNota} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">
                {itemSelecionado?.tipoPessoa === 'PJ' ? '📄 Nota Fiscal' : '🧾 Recibo'}
              </Typography>
              <Typography variant="caption" color="text.secondary">{itemSelecionado?.paciente}</Typography>
            </Box>
            <IconButton onClick={fecharModalNota}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {itemSelecionado && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Dados Básicos do Lançamento */}
              <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Descrição / Procedimento</Typography>
                    <Typography variant="body2" fontWeight={500}>{itemSelecionado.descricao}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Valor</Typography>
                    <Typography variant="h6" fontWeight={600} color="success.main">
                      {formatarMoeda(itemSelecionado.valor)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Data</Typography>
                    <Typography variant="body2">{formatarData(itemSelecionado.data)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Forma de Pagamento</Typography>
                    <Typography variant="body2">{itemSelecionado.formaPagamento}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Tipo de Pessoa</Typography>
                    <Chip
                      label={itemSelecionado.tipoPessoa}
                      size="small"
                      color={itemSelecionado.tipoPessoa === 'PF' ? 'primary' : 'secondary'}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Dados Fiscais (CPF, Pagador, etc) */}
              <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  📋 Dados Fiscais
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Beneficiário</Typography>
                    <Typography variant="body2" fontWeight={500}>{itemSelecionado.paciente}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">CPF do Beneficiário</Typography>
                    <Typography variant="body2">{itemSelecionado.cpf || 'Não informado'}</Typography>
                  </Grid>

                  {/* Dados do Pagador (se diferente) */}
                  {itemSelecionado.pagadorNome && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }}>
                          <Chip label="Pagador Diferente" size="small" color="warning" />
                        </Divider>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Nome do Pagador</Typography>
                        <Typography variant="body2" fontWeight={500}>{itemSelecionado.pagadorNome}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">CPF/CNPJ do Pagador</Typography>
                        <Typography variant="body2">{itemSelecionado.pagadorCpf}</Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="caption" color="text.secondary">Tipo</Typography>
                        <Chip label={itemSelecionado.pagadorTipoPessoa} size="small" />
                      </Grid>
                    </>
                  )}

                  {/* Observações */}
                  {itemSelecionado.observacoes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Observações</Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{itemSelecionado.observacoes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Status da Emissão */}
              {itemSelecionado.tipoPessoa === 'PF' ? (
                itemSelecionado.reciboNome ? (
                  <Alert severity="success" icon={<CheckCircleIcon />}
                    action={
                      <Button size="small" color="success" startIcon={<DownloadIcon />}
                        onClick={() => downloadReciboPF(itemSelecionado.id)}>
                        Baixar
                      </Button>
                    }
                  >
                    <Typography variant="body2" fontWeight={500}>
                      Recibo emitido: {itemSelecionado.reciboNome}
                    </Typography>
                    <Typography variant="caption">Clique em Baixar para visualizar</Typography>
                  </Alert>
                ) : (
                  <Alert severity="info" icon={<InfoIcon />}>
                    <Typography variant="body2" fontWeight={500}>Recibo ainda não emitido</Typography>
                    <Typography variant="caption">O administrador irá emitir o recibo no eCAC e anexar aqui</Typography>
                  </Alert>
                )
              ) : itemSelecionado.notaEmitida ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  <Typography variant="body2" fontWeight={500}>
                    Documento já emitido: {itemSelecionado.numeroNota}
                  </Typography>
                  <Typography variant="caption">Você pode baixar ou enviar por email</Typography>
                </Alert>
              ) : (
                <Alert severity="info" icon={<InfoIcon />}>
                  <Typography variant="body2" fontWeight={500}>Nota Fiscal não emitida</Typography>
                  <Typography variant="caption">Clique em "Emitir Nota Fiscal"</Typography>
                </Alert>
              )}


              {/* Estimativa de Impostos */}
              {itemSelecionado.tipoPessoa === 'PJ' && !itemSelecionado.notaEmitida && (
                <Paper sx={{ p: 2, bgcolor: 'warning.light', border: 1, borderColor: 'warning.main' }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="600">💡 Estimativa de Impostos</Typography>
                  <Divider sx={{ my: 1 }} />
                  {aliquotaPJ == null ? (
                    <Typography variant="caption" color="text.secondary">Calculando alíquota real...</Typography>
                  ) : (
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption">Valor Bruto:</Typography>
                        <Typography variant="body2" fontWeight={600}>{formatarMoeda(itemSelecionado.valor)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption">Impostos (≈{aliquotaPJ.toFixed(2)}%):</Typography>
                        <Typography variant="body2" fontWeight={600} color="warning.dark">
                          {formatarMoeda(itemSelecionado.valor * (aliquotaPJ / 100))}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={itemSelecionado?.notaEmitida ? handleBaixarDocumento : undefined}
            disabled={!itemSelecionado?.notaEmitida}
          >
            Baixar PDF
          </Button>
          {itemSelecionado?.notaEmitida && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancelarNota}
              disabled={loading}
            >
              {loading ? 'Cancelando...' : 'Cancelar Nota'}
            </Button>
          )}
          {itemSelecionado?.tipoPessoa === 'PJ' && !itemSelecionado?.notaEmitida && (
            <Button
              variant="contained"
              startIcon={<ReceiptIcon />}
              onClick={handleEmitirNota}
              disabled={loading}
              color="secondary"
            >
              {loading ? 'Emitindo...' : 'Emitir Nota Fiscal'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog: dados da clínica incompletos */}
      <Dialog
        open={alertClinica.open}
        onClose={() => setAlertClinica({ open: false, campos: [] })}
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 400 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <InfoIcon color="warning" />
          <Typography fontWeight={600} fontSize={16}>Dados da clínica incompletos</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Para emitir nota fiscal, preencha os seguintes campos no seu perfil:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
            {alertClinica.campos.map(c => (
              <Chip key={c} label={c} size="small" variant="outlined" color="warning" />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAlertClinica({ open: false, campos: [] })} variant="text" size="small">
            Depois
          </Button>
          <Button
            onClick={() => { setAlertClinica({ open: false, campos: [] }); navigate('/perfil'); }}
            variant="contained"
            size="small"
            disableElevation
            sx={{ borderRadius: 2 }}
          >
            Ir para Perfil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Faturamento;
